<template>
  <div class="draw-num-list">
    <!-- 彩票类型切换标签：默认双色球 -->
    <div class="type-tabs">
      <div
        v-for="t in typeList"
        :key="t.value"
        class="type-tab"
        :class="{ active: lotteryType === t.value }"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <!-- 开奖号码列表 -->
    <div class="draw-list">
      <div v-for="row in rows" :key="row.drawNumIssue" class="draw-item">
        <!-- 期号 + 开奖时间 -->
        <div class="draw-head">
          <span class="draw-expect">第 {{ row.drawNumIssue }} 期</span>
          <span class="draw-time">{{ row.drawNumTime }}</span>
        </div>
        <!-- 号码球：drawNumDTO.redNum 主号码红色，blueNum 辅助号码（如双色球蓝球）蓝色 -->
        <div class="draw-nums">
          <span v-for="(n, i) in mainNums(row.drawNumDTO)" :key="'m' + i" class="num-ball">{{ n }}</span>
          <template v-if="extraNums(row.drawNumDTO).length">
            <span class="num-plus">+</span>
            <span v-for="(n, i) in extraNums(row.drawNumDTO)" :key="'e' + i" class="num-ball blue">{{ n }}</span>
          </template>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="!loading && rows.length === 0" class="empty">暂无开奖数据</div>

      <!-- 加载更多：点一次多取一页，取完提示没有更多 -->
      <div
        v-if="rows.length > 0"
        class="load-more"
        @click="loadMore"
      >
        {{ hasMore ? (loading ? '加载中...' : '加载更多') : '— 没有更多了 —' }}
      </div>
    </div>

    <!-- 请求失败提示 -->
    <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>
  </div>
</template>

<script>
import { getDrawNumPage } from '@/api/lottery'
// 彩票类型枚举统一从工具文件引入
import { lotteryTypeList } from '@/utils/lottery'

export default {
  name: 'DrawNumListView',
  data() {
    return {
      // 当前选中的彩票类型，默认双色球（请求参数名 lotteryType）
      lotteryType: 'ssq',
      // 彩票类型枚举（统一维护在 @/utils/lottery）
      typeList: lotteryTypeList,
      rows: [],     // 当前列表数据（加载更多时往后追加）
      total: 0,     // 后端返回的总条数
      pageNum: 1,   // 当前页码
      pageSize: 10, // 每页条数
      loading: false,
      errorMsg: ''
    }
  },
  computed: {
    // 已加载条数少于总数，说明还能继续加载
    hasMore() {
      return this.rows.length < this.total
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    // 主号码：取开奖号码对象 drawNumDTO 里的 redNum，按逗号拆成数组
    mainNums(drawNumDTO) {
      return (drawNumDTO && drawNumDTO.redNum ? drawNumDTO.redNum : '')
        .split(',').filter(Boolean)
    },
    // 辅助号码：取 drawNumDTO 里的 blueNum（如双色球蓝球），没有则返回空数组
    extraNums(drawNumDTO) {
      return (drawNumDTO && drawNumDTO.blueNum ? drawNumDTO.blueNum : '')
        .split(',').filter(Boolean)
    },
    // 请求开奖号码列表
    async fetchList() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getDrawNumPage({
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          lotteryType: this.lotteryType
        })
        // 后端返回格式：{ code: "200", data: { total, rows }, msg, success }
        if (res.code === '200' || res.success) {
          this.rows = this.rows.concat(res.data.rows || [])
          this.total = res.data.total || 0
        } else {
          this.errorMsg = res.msg || '请求失败，请重试'
        }
      } catch (err) {
        this.errorMsg = '网络错误，请稍后重试'
        console.error(err)
      } finally {
        this.loading = false
      }
    },
    // 切换彩票类型：清空数据后重新从第一页请求
    switchType(value) {
      if (this.lotteryType === value) return
      this.lotteryType = value
      this.rows = []
      this.total = 0
      this.pageNum = 1
      this.fetchList()
    },
    // 加载下一页，追加到列表
    loadMore() {
      if (!this.hasMore || this.loading) return
      this.pageNum += 1
      this.fetchList()
    }
  }
}
</script>

<style scoped>
/* ===== 类型切换标签 ===== */
.type-tabs {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  /* 类型超过 4 个时横向滑动查看，一屏最多显示 4 个 */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch; /* iOS 滑动惯性 */
  scrollbar-width: none; /* Firefox 隐藏滚动条 */
}

/* Chrome/Safari 隐藏滚动条但保留滑动能力 */
.type-tabs::-webkit-scrollbar {
  display: none;
}

.type-tab {
  /* 固定占 1/4 宽度，一屏正好 4 个；超出部分横向滑动，不被挤压 */
  flex: 0 0 25%;
  box-sizing: border-box;
  text-align: center;
  padding: 12px 4px;
  font-size: 14px;
  color: #606266;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

/* 选中态：主题色文字 + 底部短横线 */
.type-tab.active {
  color: #667eea;
  font-weight: bold;
}

.type-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 28px;
  height: 3px;
  border-radius: 2px;
  background: #667eea;
}

/* ===== 号码列表 ===== */
.draw-list {
  padding: 12px;
}

.draw-item {
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
}

.draw-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.draw-expect {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
}

.draw-time {
  font-size: 12px;
  color: #909399;
}

/* 号码球：允许换行（快乐8一行最多20个号码） */
.draw-nums {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.num-ball {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f56c6c; /* 主号码：红色球 */
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.num-ball.blue {
  background: #409eff; /* 辅助号码：蓝色球 */
}

.num-plus {
  color: #909399;
  font-weight: bold;
}

/* 空状态 */
.empty {
  text-align: center;
  color: #909399;
  font-size: 13px;
  padding: 40px 0;
}

/* 加载更多 */
.load-more {
  text-align: center;
  color: #667eea;
  font-size: 13px;
  padding: 14px 0;
  cursor: pointer;
}

/* 请求失败提示 */
.error-msg {
  text-align: center;
  color: #f56c6c;
  font-size: 13px;
  padding: 20px 0;
}

/* 手机端适配 */
@media screen and (max-width: 480px) {
  .num-ball {
    width: 28px;
    height: 28px;
    font-size: 11px;
  }
}
</style>
